import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { recuentosAPI, ZONAS, LUGAR_FLAGS, parseCantidad, errorMsg } from '@/lib/recuentos';

const EMPTY = {
  orden: '', zona: ZONAS[0], id_justo: '', nombre_justo: '', unidad: 'kg', activo: true,
  unidad_alternativa: '', factor_alternativa: '',
  cuenta_angamos: true, cuenta_mall_paseo: true, cuenta_grecia_principal: true, cuenta_grecia_central: true,
};

export const IngredienteDialog = ({ open, onOpenChange, ingrediente, nextOrden, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(ingrediente
      ? { ...ingrediente, unidad_alternativa: ingrediente.unidad_alternativa || '', factor_alternativa: ingrediente.factor_alternativa ?? '' }
      : { ...EMPTY, orden: nextOrden });
  }, [open, ingrediente, nextOrden]);

  const save = async () => {
    const factor = parseCantidad(f.factor_alternativa);
    if (Number.isNaN(factor)) return toast.error('Factor inválido');
    if (f.unidad_alternativa && !factor) return toast.error('Indica el factor de la unidad alternativa');
    const payload = {
      ...f, orden: parseInt(f.orden, 10) || 0,
      unidad_alternativa: f.unidad_alternativa || null,
      factor_alternativa: f.unidad_alternativa ? factor : null,
    };
    setSaving(true);
    try {
      if (ingrediente) {
        const { id, id_justo, created_at, updated_at, ...upd } = payload; // eslint-disable-line no-unused-vars
        await recuentosAPI.updateIngrediente(ingrediente.id, upd);
      } else {
        await recuentosAPI.createIngrediente(payload);
      }
      toast.success('Ingrediente guardado');
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errorMsg(e, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="ingrediente-dialog">
        <DialogHeader><DialogTitle>{ingrediente ? 'Editar ingrediente' : 'Nuevo ingrediente'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label>ID Justo (exacto)</Label>
            <Input value={f.id_justo} onChange={set('id_justo')} disabled={!!ingrediente} className="font-mono text-xs" data-testid="ing-id-justo-input" />
          </div>
          <div className="col-span-2">
            <Label>Nombre Justo (exacto)</Label>
            <Input value={f.nombre_justo} onChange={set('nombre_justo')} data-testid="ing-nombre-input" />
          </div>
          <div>
            <Label>Zona</Label>
            <Select value={f.zona} onValueChange={(v) => setF({ ...f, zona: v })}>
              <SelectTrigger data-testid="ing-zona-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ZONAS.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Orden</Label><Input type="number" value={f.orden} onChange={set('orden')} data-testid="ing-orden-input" /></div>
            <div>
              <Label>Unidad</Label>
              <Select value={f.unidad} onValueChange={(v) => setF({ ...f, unidad: v })}>
                <SelectTrigger data-testid="ing-unidad-select"><SelectValue /></SelectTrigger>
                <SelectContent>{['kg', 'litros', 'unidades'].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Unidad alternativa (opcional)</Label><Input placeholder="ej. pack de 6" value={f.unidad_alternativa} onChange={set('unidad_alternativa')} data-testid="ing-alt-input" /></div>
          <div><Label>Factor</Label><Input inputMode="decimal" placeholder="ej. 6" value={f.factor_alternativa} onChange={set('factor_alternativa')} data-testid="ing-factor-input" /></div>
          <div className="col-span-2">
            <Label>Se cuenta en</Label>
            <div className="flex flex-wrap gap-4 mt-2">
              {LUGAR_FLAGS.map((l) => (
                <label key={l.field} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!f[l.field]} onCheckedChange={(c) => setF({ ...f, [l.field]: !!c })} data-testid={`ing-flag-${l.codigo}`} /> {l.label}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm col-span-2">
            <Checkbox checked={!!f.activo} onCheckedChange={(c) => setF({ ...f, activo: !!c })} data-testid="ing-activo-checkbox" /> Activo
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="ing-guardar-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
